<template>
  <v-dialog
    :value="visiblePopup"
    :max-width="1000"
    scrollable
    @keydown.esc="onCloseClickButton"
    @click:outside="onCloseClickButton"
  >
    <v-card class="degrees-domain-popup popup-wrapper">
      <div class='close-button-section'>
        <span class='close_btn' @click.stop='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class='degrees-domain-popup__title'> {{ this.$t('360.degrees.popup.title') }} </div>
      <div class='degrees-domain-popup__subtitle-wrapper'>
        <div class='degrees-domain-popup__subtitle'>
          <div class='degrees-domain-popup__subtitle__label'> {{ this.$t('360.degrees.popup.subtitle')}}</div>
          <div class='degrees-domain-popup__subtitle__data'>{{ organizationName }}</div>
          <div v-if="dataVersion===2" class='degrees-domain-popup__subtitle__label size-reduce'> {{ this.$t('360.degrees.popup.identifier')}}</div>
          <div v-if="dataVersion===2" class='degrees-domain-popup__subtitle__data size-reduce'>{{ identifierName }}</div>
        </div>
        <div class='degrees-domain-popup__subtitle-wrapper__note' v-if='dataAvailableForChart'>
          {{ this.$t('360.degrees.popup.content.note')}}
        </div>
        <download-c-s-v class="download-csv-btn" v-if='dataAvailableForChart' @click="downloadCSVReport"/>
      </div>
      <v-card-text>
        <div class='degrees-domain-popup__content'>
          <div class='degrees-domain-popup__content__coverage'>
            <div class='degrees-domain-popup__content__coverage__title'>
              {{ this.$t('360.degrees.popup.totals.title') }}
              <span class='total-domains'> {{this.stats.resolved + this.stats.unresolved}} </span>
            </div>
            <div class='degrees-domain-popup__content__coverage__totals'>
              <totals-indicator
                :title='"360.degrees.popup.totals.subtitle.resolved"'
                :data='this.stats.resolved'
              />
              <totals-indicator
                :title='"360.degrees.popup.totals.subtitle.unresolved"'
                :data='this.stats.unresolved'
              />
            </div>
            <v-divider class='degrees-domain-popup__divider' inset />
            <div v-if='dataAvailableForChart'>
              <div class='degrees-domain-popup__content__coverage__title'> {{ this.$t('360.degrees.popup.coverage.title') }}</div>
              <div v-for='key in statsKeys' :key='`${key}-${Math.random()}`'>
                <g-e-o-coverage-indicator v-if='stats[key].subdomains > 0'
                                          :country='key !== "undefined"? key : "Global"'
                                          :code="stats[key].code"
                                          :total='stats[key].total'
                                          :covered='stats[key].coverage'
                                          :selected="selectedCountry"
                />
              </div>
              <div v-if='hasMore' class='degrees-domain-popup__show-hide' @click.stop="showMore = !showMore"> {{$t(getCaption)}}</div>
              <div class='degrees-domain-popup__add-domain' v-if='isDomainSelected'>
                <v-divider class='degrees-domain-popup__divider' inset />
                <div class='degrees-domain-popup__content__coverage__title'> {{ this.$t('configuration.add.domain') }}</div>
                <div class='degrees-domain-popup__add-domain__selected-domain '>
                  <span> {{ getSelectedDomain}} </span>
                  <a
                    :href="`https://${getSelectedDomain}`"
                    target="_blank"
                  >
                    <v-icon>open_in_new</v-icon>
                  </a>
                </div>
                <div>
                  <v-btn class="degrees-domain-popup__add-domain__btn" @click="addDomain" :disabled="isCovered">
                    {{ $t('360.degrees.popup.cover.domain') }}
                  </v-btn>
                </div>
                <div v-if='isCovered' class='degrees-domain-popup__add-domain__message'> {{ $t('360.degrees.popup.cover.domain.message') }}</div>
              </div>
              <div class='degrees-domain-popup__cover-region' v-if='isCountrySelected'>
                <v-divider class='degrees-domain-popup__divider' inset />
                <div class='degrees-domain-popup__content__coverage__title'>
                  {{ $t('configuration.cover.region') }}
                </div>
                <div class='degrees-domain-popup__cover-region__selected-country'>
                  <span> {{ selectedCountry }} </span>
                </div>
                <div class='degrees-domain-popup__cover-region__domains-count'>
                  {{ getUnaddedDomainsInRegion }} {{ $t('360.degrees.popup.domains.will.be.added') }}
                </div>
                <div>
                  <v-btn
                    class="degrees-domain-popup__cover-region__btn"
                    @click="coverRegion"
                    :disabled="areAllDomainsInRegionCovered"
                  >
                    {{ $t('360.degrees.popup.cover.region') }}
                  </v-btn>
                </div>
                <div v-if='areAllDomainsInRegionCovered' class='degrees-domain-popup__cover-region__message'>
                  {{ $t('360.degrees.popup.cover.region.message') }}
                </div>
              </div>
            </div>
          </div>
         <div class='degrees-domain-popup__chart-section'>
           <div class="degrees-domain-popup__chart-section__slider" v-if='(dataVersion===2) && dataAvailableForChart'>
             <div class='degrees-domain-popup__subtitle__label'> {{ this.$t('360.degrees.popup.confidence')}}</div>
            <v-slider
              v-model='confidenceIndex'
              :tick-labels="tickLabels"
              step="1"
              ticks="always"
              :tick-size='tickLabels.length'
              :max="tickLabels.length - 1"
              min="0"
            />
           </div>
           <div v-else style="height: 15px" />
           <zoomable-circle-packing-chart
             v-if='dataAvailableForChart'
             :data = "chart"
             :width = '700'
             :height = '700'
             :update = "Boolean(isUpdated)"
             @domainSelected = 'domainSelected'
             @chartUpdated = 'chartUpdated'
             @countrySelected = 'countrySelected'
           />
         </div>
        </div>
        <div class='degrees-domain-popup__no-data' v-if='!dataAvailableForChart'>
          <div class='degrees-domain-popup__no-data__title'>
            {{ $t('360.no.chart.data.title') }}
          </div>
          <div class='degrees-domain-popup__no-data__subtitle'>
            {{ $t('360.no.chart.data.subtitle') }}
          </div>
          <div class='degrees-domain-popup__no-data__action'>
            <download-c-s-v class="download-csv-btn" @click="downloadCSVReport"/>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import ZoomableCirclePackingChart from '@/components/charts/ZoomableCirclePackingChart.vue';
import {
  ADD_URL_CONFIGURATIONS, LOAD_DOMAINS_FROM_CONFIGURATION,
  LOAD_PANORAMIC_DATA,
} from '@/store/project/actionTypes';
import GEOCoverageIndicator from '@/components/panoramic-view/GEOCoverageIndicator.vue';
import TotalsIndicator from '@/components/panoramic-view/TotalsIndicator.vue';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import moment from 'moment';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'ThreeHundredSixtyDegreesPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    TotalsIndicator, GEOCoverageIndicator, ZoomableCirclePackingChart, DownloadCSV,
  },
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    website: {
      type: String,
      required: true,
      default: '',
    },
    subdomainTotalStats: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  data() {
    return {
      chart: {},
      updated: false,
      stats: {},
      selectedDomain: '',
      showMore: true,
      selectedCountry: '',
      domainAdded: false,
      confidence: 95,
    };
  },
  async created() {
    await this.$store.dispatch(LOAD_PANORAMIC_DATA, { domain: [this.website] });
  },
  computed: {
    ...mapGetters(['domains', 'configurationDomains', 'panoramicData']),
    timezonesItems() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    dataVersion() {
      if (this?.panoramicData && 'resolved' in this.panoramicData) {
        return 1;
      }
      if (this?.panoramicData && '100' in this.panoramicData) {
        return 2;
      }
      return 0;
    },
    tickLabels() {
      const labels = [];
      Object.keys(this?.panoramicData).forEach(key => {
        if (this.panoramicData[key].length > 0) {
          labels.push(key);
        }
      });
      return labels;
    },
    confidenceIndex: {
      get() {
        const index = this.tickLabels.indexOf(String(this.confidence));
        return index >= 0 ? index : 0;
      },
      set(index) {
        this.confidence = this.tickLabels[index] || this.tickLabels[0];
      },
    },
    statsKeys() {
      const filtered = this.filteredStats;
      return this.showMore ? filtered.slice(0, 9) : filtered;
    },
    hasMore() {
      const filtered = this.filteredStats;
      return filtered.length > 9;
    },
    filteredStats() {
      const keys = Object.keys(this.stats);
      const filtered = keys.filter(key => this.stats[key].subdomains > 0);
      return filtered;
    },
    dataAvailableForChart() {
      return this.chart?.children?.length && this.stats.resolved && this.stats.resolved > 0;
    },
    organizationName() {
      return (this.dataVersion === 2 ? this.panoramicData?.['95']?.[0]?.identifier : this.panoramicData?.org) || this.website;
    },
    identifierName() {
      if (this.dataVersion !== 2) {
        return '';
      }
      const identifier = this.panoramicData[String(this.confidence)]?.[0]?.identifier;
      const knowList = ['Twisted-Domain', 'Internet', 'Original'];
      return knowList.includes(identifier) ? identifier : 'Registrar';
    },
    listOfDomains() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
    isCovered() {
      return this.listOfDomains.includes(this.selectedDomain);
    },
    isUpdated() {
      return this.updated;
    },
    getSelectedDomain() {
      return this.selectedDomain;
    },
    isDomainSelected() {
      return this.selectedDomain.length > 0;
    },
    getCaption() {
      return (this.showMore ? 'infrastructure.technologies.show.all.list' : 'infrastructure.technologies.hide.list');
    },
    isCountrySelected() {
      return this.selectedCountry && this.selectedCountry.length > 0;
    },
    getUnaddedDomainsInRegion() {
      if (!this.selectedCountry || !this.chart.children) return 0;
      const countryData = this.chart.children.find(
        child => child.name.toLowerCase() === this.selectedCountry.toLowerCase(),
      );
      if (!countryData) return 0;
      // Count domains that are NOT already covered
      return countryData.children.filter(domain => !domain.covered).length;
    },
    areAllDomainsInRegionCovered() {
      return this.getUnaddedDomainsInRegion === 0;
    },
  },
  watch: {
    panoramicData: {
      async handler() {
        this.updated = false;
        this.chart = await this.createData();
        this.updated = true;
      },
      deep: true,
    },
    async confidence() {
      if (this.confidence === undefined || this.confidence === null) {
        return;
      }
      this.updated = false;
      this.chart = await this.createData();
      this.updated = true;
    },
    async visiblePopup() {
      if (this.visiblePopup === true && this.website) {
        await this.$store.dispatch(LOAD_PANORAMIC_DATA, { domain: [this.website] });
      }
      this.selectedDomain = '';
      this.domainAdded = false;
    },
    listOfDomains: {
      async handler() {
        this.updated = true;
      },
    },
    deep: true,
  },
  methods: {
    chartUpdated() {
      this.updated = false;
    },
    domainSelected(e) {
      this.selectedDomain = e;
    },
    countrySelected(e) {
      this.selectedCountry = e;
    },
    async onCloseClickButton() {
      await this.$emit('close', this.domainAdded);
    },
    calculateTimeZone() {
      const defaultBrowserTimeZoneValue = String(moment().utcOffset());
      const defaultBrowserTimeZoneObj = this.timezonesItems.find(timeZoneObj => timeZoneObj.value === defaultBrowserTimeZoneValue);
      return defaultBrowserTimeZoneObj?.id || null;
    },
    async addDomain() {
      const timezone = this.calculateTimeZone();
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: [{
          data: this.selectedDomain, metaData: [], tld: this.selectedDomain, tags: { [this.organizationName]: 'added' }, addedFromThreeHundredSixtyTld: this.website,
        }],
        configType: CONFIGURATION.CONFIG_TYPES.DOMAIN,
        scanEmployees: false,
        successMessageType: CONFIGURATION_ACTION_TYPES.ADD_DOMAIN,
        displayMessage: true,
        timezone,
      });
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      this.domainAdded = true;
    },
    async coverRegion() {
      if (!this.selectedCountry || !this.chart.children) return;
      const timezone = this.calculateTimeZone();
      // Find the country data
      const countryData = this.chart.children.find(
        child => child.name.toLowerCase() === this.selectedCountry.toLowerCase(),
      );
      if (!countryData) return;
      // Get all uncovered domains in this region (filter out already covered ones)
      const domainsToAdd = countryData.children
        .filter(domain => !domain.covered)
        .map(domain => ({
          data: domain.name,
          metaData: [],
          tld: domain.name,
          tags: { [this.organizationName]: 'added' },
          addedFromThreeHundredSixtyTld: this.website,
          addedFromThreeHundredSixtyRegion: this.selectedCountry,
        }));
      if (domainsToAdd.length === 0) return;
      // Add all domains in the region
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: domainsToAdd,
        configType: CONFIGURATION.CONFIG_TYPES.DOMAIN,
        scanEmployees: false,
        successMessageType: CONFIGURATION_ACTION_TYPES.COVER_REGION,
        displayMessage: true,
        timezone,
      });
      // Reload domains to update the UI
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      this.domainAdded = true;
    },
    async createData() {
      let chart = {};
      this.stats = {};
      let rowData = this.dataVersion === 2 ? this.panoramicData[String(this.confidence)]?.[0] : this.panoramicData;
      // try to find another confidence level
      if (!rowData) {
        const data = this.panoramicData || {};
        let availableConfidence = Object.keys(data)
          .sort((a, b) => Number(b) - Number(a))
          .filter(k => String(k) !== '100')
          .find(k => Array.isArray(data[k]) && data[k].length > 0);
        if (!availableConfidence && Object.prototype.hasOwnProperty.call(data, '100')) {
          availableConfidence = '100';
        }
        this.confidence = availableConfidence;
      }
      // second attempt
      rowData = this.dataVersion === 2 ? this.panoramicData[String(this.confidence)]?.[0] : this.panoramicData;
      if (!rowData) {
        return chart;
      }
      const identifier = (this.dataVersion === 2 ? rowData?.identifier : rowData?.org) || this.website;
      chart = {
        name: identifier,
        children: [],
      };
      const geoAggregation = [];
      this.stats.resolved = Object.keys(rowData?.resolved).length;
      this.stats.unresolved = Object.keys(rowData?.['non-resolved']).length;
      Object.keys(rowData?.resolved).forEach(tld => {
        const item = rowData?.resolved?.[tld]?.[1];
        const countryName = this.dataVersion === 2 ? item?.country_name : item?.country;
        const countryCode = this.dataVersion === 2 ? item?.country : item?.countryCode; // whichever is "code"

        if (!geoAggregation[countryName]) {
          geoAggregation[countryName] = [];
          this.stats[countryName] = {
            total: 0,
            coverage: 0,
            subdomains: 0,
            code: countryCode,
          };
        }
        geoAggregation[countryName].push({
          name: tld,
          value: rowData.resolved[tld],
        });
        const covered = this.listOfDomains.includes(tld);
        if (rowData.resolved[tld][2] > 0 || covered) {
          const value = covered ? (this.subdomainTotalStats || rowData.resolved[tld][2]) : rowData.resolved[tld][2];
          this.stats[countryName].total += 1;
          this.stats[countryName].subdomains += value;
        }
        if (covered) {
          this.stats[countryName].coverage += 1;
        }
      });
      Object.keys(geoAggregation).forEach(country => {
        const childrenArray = [];
        geoAggregation[country].forEach(tld => {
          const covered = this.listOfDomains.includes(tld.name);
          if (tld.value[2] > 0 || covered) {
            let value = covered ? (this.subdomainTotalStats || tld.value[2]) : tld.value[2];
            value = (covered && (value === 0) ? 1 : value);
            childrenArray.push({
              name: tld.name,
              value,
              covered,
            });
          }
        });
        if (childrenArray.length > 0) {
          chart.children.push({
            name: country === 'undefined' ? 'Global' : country,
            children: childrenArray,
          });
        }
      });
      this.stats = Object.fromEntries(
        Object.entries(this.stats).sort(([, a], [, b]) => (((b?.coverage / b?.total) * 100) || 0) - (((a?.coverage / a?.total) * 100) || 0)),
      );
      return chart;
    },
    downloadCSVReport() {
      const rows = [];
      let rowData = this.panoramicData;
      let columns = 'TLD,Resolved (Yes/No),Covered (Yes/No),Country,Estimated Subdomains';
      if (this.dataVersion === 2) {
        columns = `${columns},Confidence,Identifier`;
      }
      rows.push(columns);
      if (this.dataVersion === 1) {
        Object.keys(rowData?.resolved)
          .forEach(tld => {
            const covered = this.listOfDomains.includes(tld);
            const value = covered ? (this.subdomainTotalStats || rowData.resolved[tld][2]) : rowData.resolved[tld][2];
            rows.push(`${tld},yes,${covered ? 'yes' : 'no'},${rowData.resolved[tld][1].country},${value}`);
          });
        Object.keys(rowData?.['non-resolved'])
          .forEach(tld => {
            rows.push(`${tld},no,N/A,N/A,N/A`);
          });
      }
      if (this.dataVersion === 2) {
        Object.keys(this.panoramicData).forEach(confidence => {
          if (this.panoramicData[String(confidence)].length > 0) {
            rowData = this.panoramicData[String(confidence)]?.[0];
            Object.keys(rowData?.resolved)
              .forEach(tld => {
                const covered = this.listOfDomains.includes(tld);
                const value = covered ? (this.subdomainTotalStats || rowData.resolved[tld][2]) : rowData.resolved[tld][2];
                rows.push(`${tld},yes,${covered ? 'yes' : 'no'},${rowData.resolved[tld][1].country_name},${value},${confidence},${rowData.identifier}`);
              });
            Object.keys(rowData?.['non-resolved'])
              .forEach(tld => {
                rows.push(`${tld},no,N/A,N/A,N/A,${confidence},${rowData.identifier}`);
              });
          }
        });
      }
      const csvContent = `data:text/csv;charset=utf-8,${rows.join('\n')}`;
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', '360-report.csv');
      document.body.appendChild(link);
      link.click();
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.degrees-domain-popup {
  &__show-hide{
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: rem(14);
    letter-spacing: 0;
    &:hover {
      cursor: pointer;
    }
  }
  &.popup-wrapper {
    max-width: rem(1000);
    display: flex;
    flex-direction: column;
    .close-button-section {
      z-index: 2;
      padding-top: rem(15);
      padding-right: rem(15);
      .close_btn {
        float: right;
        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }
  }
  &__add-domain{
    margin-top: rem(10);
    &__btn {
      margin-top: rem(10);
      background-color: var(--brand-primary) !important;
      font-size: rem(12);
      color: #ffffff;
      font-family: $font-family-medium;
      margin-bottom: rem(10);
    }
    &__message {
      color: var(--severity-low);
    }
    &__selected-domain {
      text-transform: uppercase;
      max-width: rem(180);
      span{
        max-width: rem(180);
      }
      color: var(--text-primary);
      font-family: $font-family-medium;
      display: flex;
      flex-direction: row;
      .v-icon{
        font-size: rem(12);
        padding-left: rem(5);
      }
    }
  }
  &__cover-region {
    margin-top: rem(10);
    margin-bottom: rem(20);
    &__btn {
      margin-top: rem(10);
      background-color: var(--brand-primary) !important;
      font-size: rem(12);
      color: #ffffff;
      font-family: $font-family-medium;
      margin-bottom: rem(5);
    }
    &__message {
      color: var(--severity-low);
    }
    &__selected-country {
      text-transform: uppercase;
      max-width: rem(180);
      color: var(--text-primary);
      font-family: $font-family-medium;
      display: flex;
      flex-direction: row;
    }
    &__domains-count {
      color: var(--text-tertiary);
      font-family: $font-family-regular;
      font-size: rem(12);
      margin-top: rem(5);
    }
  }
  &__subtitle-wrapper{
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    .download-csv-btn {
      height: rem(32);
      margin-right: rem(25);
      margin-top: rem(15);
      &:hover {
        cursor: pointer;
      }
    }
    &__note{
      margin-top: rem(15);
      font-family: $font-family-regular;
      font-size: rem(12);
      max-width: rem(500);
    }
  }
  &__divider {
    margin-left: 0 !important;
    margin-bottom: rem(15);
  }
  &__title {
    margin-top: rem(-10);
    padding-bottom: rem(15);
    padding-left: rem(35);
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: rem(24);
    font-weight: bold;
    line-height: rem(20);
    letter-spacing: 0;
  }
  &__chart-section{
    &__slider {
      .degrees-domain-popup__subtitle__label {
        margin-bottom: rem(5);
      }

      margin-left: rem(15);
      margin-right: rem(20);

      ::v-deep .v-slider__tick-label {
        font-family: $font-family-semibold;
        font-size: rem(14);
        padding-top: rem(4);
      }

      ::v-deep .v-slider__tick {
        width: rem(8) !important;
        height: rem(8) !important;
        border-radius: 50%;
        background-color: var(--severity-low);
        top: calc(50% - 4px) !important;
      }

      ::v-deep .v-slider__tick:not(:last-child) {
        .v-slider__tick-label {
          transform: translateX(-5px);
        }
      }
      ::v-deep .v-slider__tick:last-child {
        .v-slider__tick-label {
          transform: translateX(-10px);
        }
      }
    }
  }
  &__subtitle{
    margin-bottom: rem(15);
    padding-left: rem(35);
    &__label{
      color: var(--text-tertiary);
      font-family: $font-family-regular;
      letter-spacing: rem(1.1);
      line-height: rem(15);
      text-transform: uppercase;
      font-size: rem(12);
      &.size-reduce {
        font-size: rem(10);
        line-height: rem(14);
      }
    }
    &__data{
      color: var(--text-primary);
      font-family: $font-family-medium;
      font-size: rem(15);
      text-transform: uppercase;
      overflow-wrap: break-word;
      overflow: auto;
      &.size-reduce {
        font-size: rem(10);
        line-height: rem(12);
      }
    }
    &.size-reduce {
      font-size: rem(12);
      line-height: rem(14);
    }
  }
  &__no-data {
    text-align: center;
    color: var(--text-tertiary);
    font-family: $font-family-regular;
    font-size: rem(30) !important;
    line-height: rem(40);
    min-width: rem(650);
    position: absolute;
    top: 48%;
    left: 18%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    &__subtitle {
      font-family: $font-family-regular;
      font-size: rem(16) !important;
    }
    &__action {
      .download-csv-btn {
        height: rem(32);
        margin-right: rem(25);
        margin-top: rem(15);
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
  &__content {
    min-width: rem(500);
    min-height: rem(700);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    &__coverage {
      min-width: rem(220);
      padding-left: rem(10);
      &__title {
        color: var(--text-primary);
        font-family: $font-family-black;
        font-size: rem(18);
        line-height: rem(20);
        letter-spacing: 0;
        margin-bottom: rem(15);
        text-transform: capitalize;
        .total-domains {
          font-family: $font-family-semibold !important;
        }
      }
      &__totals {
        display: flex;
        flex-direction: row;
        padding-bottom: rem(25);
      }
    }
  }
}
.fixed-height{
  max-height: 92% !important;
}
</style>
<style lang='scss'>
.v-dialog.v-dialog--active.v-dialog--scrollable{
  max-height: 96% !important;
}
.degrees-domain-popup {
  .v-card__text {
    padding: 0 24px 2px !important;
  }
  .txt-record {
    .v-text-field {
      margin-top: 0px !important;
      height: 10px;
      font-size: 12px;
    }
    .v-text-field.v-text-field--solo .v-input__control {
      min-height: 20px !important;
    }
    .v-input__slot {
      min-width: 110px;
      max-width: 110px;
    }
  }
  .value-record {
    .v-text-field {
      margin-top: 0px !important;
      height: 10px;
      font-size: 12px;
    }
    .v-text-field.v-text-field--solo .v-input__control {
      min-height: 20px !important;
    }
    .v-input__slot {
      min-width: 280px;
    }
  }
}
</style>
